import { defaults } from "@helpers/defaults";
import {
  getBounds,
  hasTouchScreen,
  showDistanceToPlace,
} from "@utils/function";
import ZoomOnMobileOrDesktop from "./zoomMobileOrDesktop";
import addZoomAtributei18n from "./zoomAttribute";

/**
 * https://tree.taiga.io/project/eventhubhq-eventhubclient/issue/229
 * We had to turn off canvas because the
 * offline library does not work with the canvas library
 */

// https://github.com/GIAPspzoo/L.TileLayer.Canvas
import "./tileLayerCanvas";

let { latlng, latlng_mobile, maxZoom, minZoom } = HQ.Properties.mapCoordinates;

latlng_mobile = latlng_mobile == null ? latlng : latlng_mobile;

// https://tree.taiga.io/project/eventhubhq-eventhubclient/issue/365
const [lat, lng] = (hasTouchScreen() ? latlng_mobile : latlng)
  .split(",")
  .map(Number);

window.appId = HQ.Properties.appId;

// --------------------------------------------------
// generate map

const map = L.map("map", {
  center: [lat, lng],
  zoomControl: false, // zoom control off
  zoomDelta: 0.25,
  zoomSnap: 0.1,
  maxZoom,
  minZoom,
  zoom: ZoomOnMobileOrDesktop,
  tap: false,
  // attributionControl: false,
});

// --------------------------------------------------
// move zoom to top right

L.control.zoom({ position: "topright" }).addTo(map);

// --------------------------------------------------
// custom layer
// `https://tile.openstreetmap.org/{z}/{x}/{y}.png?key=${defaults.baseMapKey}`,

if (defaults.baseMapUrl) {
  const detectTypeOfMapsBing = defaults.baseMapUrl?.includes("bing");
  const detectTypeOfMapsAzure = defaults.baseMapUrl?.includes("azuremaps");
  const detectTypeOfMapsStadia = defaults.baseMapUrl?.includes("stadiamaps");

  if (detectTypeOfMapsBing) {
    const defaultsConfig = {
      key: defaults.baseMapKey,
      detectRetina: true,
    };

    L.bingLayer(
      L.extend({}, defaultsConfig, {
        imagerySet: "Aerial",
        style: HQ.Properties.styleBingMap,
        culture: "en",
      })
    ).addTo(map);
  } else if (detectTypeOfMapsAzure) {
    // https://github.com/Azure-Samples/azure-maps-leaflet
    let layer = new L.TileLayer.AzureMaps({
      authOptions: {
        authType: "subscriptionKey",
        subscriptionKey: defaults.baseMapKey,
      },
    });

    layer.setTilesetId(HQ.Properties.styleAzureMaps);

    map.addLayer(layer);

    // Use the static function for creating Azure Maps tile layer easily.
    // L.tileLayer.azureMaps({ authOptions }).addTo(map);
  } else {
    const stadiamaps = detectTypeOfMapsStadia
      ? '&copy; <a href="https://stadiamaps.com/" target="_blank">Stadia Maps</a>, '
      : "";

    const configOption = {
      attribution: `${stadiamaps}${defaults.baseMapAttribution}`,
      maxZoom,
      minZoom,
      edgeBufferTiles: 5,
    };

    // https://tree.taiga.io/project/eventhubhq-eventhubclient/issue/603
    // #603 Basemap without keys
    const key = defaults.baseMapKey ? `?key=${defaults.baseMapKey}` : "";
    L.tileLayer(`${defaults.baseMapUrl}${key}`, configOption).addTo(map);
  }
}

// --------------------------------------------------
// Custom event layer
// plugion for canvas layer
// github.com/GIAPspzoo/L.TileLayer.Canvas

if (defaults.customLink) {
  if (HQ.Properties.leafletoffline !== "1") {
    window["defaults-map"] = L.tileLayer
      .canvas(`${defaults.customLink}`, {
        doubleSize: true,
        className: "golf-layer",
        attribution: defaults.customAttribution,
        crossOrigin: true,
        minZoom,
        maxZoom,
      })
      .addTo(map);
  } else {
    const customLayer = LeafletOffline.tileLayerOffline(defaults.customLink, {
      doubleSize: true,
      className: "golf-layer",
      attribution: defaults.customAttribution,
      crossOrigin: true,
      minZoom,
      maxZoom,
    }).addTo(map);
    const saveControl = LeafletOffline.savetiles(customLayer, {
      // optional zoomlevels to save, default current zoomlevel
      position: "topright",
      zoomlevels: findAllZoomLevels(),
      alwaysDownload: false,
      showButton: HQ.Properties.btnOffline ?? false,
      bounds: getBounds() ?? null,
      maxZoom,
      confirm(layer, successCallback) {
        // eslint-disable-next-line no-alert
        if (window.confirm(`Save ${layer._tilesforSave.length}`)) {
          successCallback();
        }
      },
      confirmRemoval(layer, successCallback) {
        // eslint-disable-next-line no-alert
        if (window.confirm("Remove all the tiles?")) {
          successCallback();
        }
      },
      saveText:
        '<svg viewBox="0 0 32 32" class="icon-offline icon-save-map"><path d="M15 22h-15v8h30v-8h-15zM28 26h-4v-2h4v2zM7 10l8-8 8 8h-5v10h-6v-10z"></path></svg>',
      rmText:
        '<svg viewBox="0 0 32 32" class="icon-offline icon-save-map"><path d="M4 10v20c0 1.1.9 2 2 2h18c1.1 0 2-.9 2-2V10H4zm6 18H8V14h2v14zm4 0h-2V14h2v14zm4 0h-2V14h2v14zm4 0h-2V14h2v14zm4.5-24H20V1.5c0-.825-.675-1.5-1.5-1.5h-7c-.825 0-1.5.675-1.5 1.5V4H3.5C2.675 4 2 4.675 2 5.5V8h26V5.5c0-.825-.675-1.5-1.5-1.5zM18 4h-6V2.025h6V4z"/></svg>',
    });
    saveControl.addTo(map);
  }
}

/**
 * find all zoom levels from HQ.Properties.mapCoordinates
 *
 * @returns {Array}
 */
function findAllZoomLevels() {
  const mapCoordinates = HQ.Properties.mapCoordinates;
  let arrayWithZoomLevels = [];
  for (const [key, value] of Object.entries(mapCoordinates)) {
    if (
      key.includes("zoom") ||
      key.includes("zoom_mobile") ||
      key.includes("minZoom") ||
      key.includes("maxZoom")
    ) {
      // round to number without decimal places because
      // offline library does not work with decimal places
      arrayWithZoomLevels.push(Math.floor(value));
    }
  }

  // remove duplicates
  const newArray = new Set(arrayWithZoomLevels);

  // find smallest and largest number
  const smallestNumber = Math.min(...newArray);
  const largestNumber = Math.max(...newArray);

  // console.log(newArray, smallestNumber, largestNumber);

  let fullZoomLevels = [];
  for (let i = smallestNumber; i <= largestNumber; i++) {
    fullZoomLevels.push(i);
  }
  return fullZoomLevels;
}

// set title for zoom in/out
addZoomAtributei18n();

window["map"] = map;

export default map;
